import { eq, useLiveQuery } from '@tanstack/react-db'
import { useState } from 'react'

import { applyAddendums, orderAddendums } from '@/db/application/addendums'

import { getAllowedJobStatusTransitions, type JobStatus } from '@/db/domain/jobStatus'
import { useDb } from '@/lib/dbContext'
import type { Db } from '@/lib/db'
import { looksLikePublicId } from '@/lib/public-id/routeLookup'
import { useEnsureServiceLoaded } from '@/screens/useEnsureServiceLoaded'
import { captureLegacyIdRoute } from '@/lib/public-id/legacyRouteSentry'
import type { Addendum, AddendumChange, Customer, Job, JobAssignment, ServiceRevision, StatusChangeLog } from '@/lib/schemas/db'

export interface JobAssignmentHistoryItem {
  id: string
  assignedAt: string
  unassignedAt?: string
  assignedUserName: string
  authorName: string
}

export type JobDetailsSavePayload = {
  contactAddress?: string
  contactName?: string
  contactPhone?: string
  contactEmail?: string
  invoiceName?: string
  invoiceAddress?: string
  invoiceEmail?: string
  invoicePhone?: string
  invoiceVat?: string
  deviceType?: string
  deviceDescription?: string
  devicePassword?: string
  fault: string
  peripherals?: string
  numberTag?: string
  invoiceId?: string
}

export interface JobDetailsScreenProps {
  showHeader?: boolean
  serviceId: string
  createdAt: string
  publicId?: string
  customerId?: string
  customerPublicId?: string
  customerName: string
  checkedInSignatureUrl?: string
  checkedOutSignatureUrl?: string
  linkedCustomer?: {
    fullName?: string
    externalId?: string
    contactEmail?: string
    contactPhone?: string
    contactAddress?: string
    invoiceName?: string
    invoiceEmail?: string
    invoicePhone?: string
    invoiceAddress?: string
    invoiceVat?: string
  }
  contactAddress?: string
  contactName?: string
  contactPhone?: string
  contactEmail?: string
  invoiceName?: string
  invoiceAddress?: string
  invoiceEmail?: string
  invoicePhone?: string
  invoiceVat?: string
  deviceType?: string
  deviceDescription?: string
  devicePassword?: string
  numberTag?: string
  fault: string
  peripherals?: string
  status: JobStatus
  statusTransitions: JobStatus[]
  statusChanges?: StatusChangeLog[]
  checkedInSignedAt?: string
  checkedInBy?: string
  checkedInByName?: string
  checkedInNoSignatureReason?: string
  checkedOutSignedAt?: string
  checkedOutBy?: string
  checkedOutByName?: string
  checkedOutName?: string
  checkedOutPhone?: string
  checkedOutNoSignatureReason?: string
  assignedUserId?: string
  assignedUserName?: string
  assignmentHistory?: JobAssignmentHistoryItem[]
  billingOutcome?: Job['billingOutcome']
  invoiceId?: string
  getAuthorDisplayName?: (authorId: string) => string
  onAssignJob?: (userId: string) => void | Promise<void>
  onSaveDetails?: (payload: JobDetailsSavePayload) => void | Promise<void>
  onChangeStatus?: (status: JobStatus, reason?: string) => void | Promise<void>
  onChangeCustomer?: (customerId: string) => void | Promise<void>
  /** Amendments recorded after this job was locked, oldest first. */
  addendums?: Addendum[]
  /** The add-only trail of edits made while the job was open, oldest first. */
  revisions?: ServiceRevision[]
  /** Values as the job was locked, before any addendum. */
  lockedValues?: Record<string, string | undefined>
  amending?: boolean
  onStartAmend?: () => void
  onDiscardAmend?: () => void
  onSaveAddendum?: (
    reason: string,
    changes: AddendumChange[],
  ) => void | Promise<void>
}

type DetailsState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'not_found' }
  | { kind: 'ready'; screenProps: JobDetailsScreenProps; db: Db }

function getJobFileUrl(job: Job, filename?: string) {
  if (!filename) return undefined

  return `/api/files/pbc_1776200000/${job.id}/${filename}`
}

export function useJobDetails(serviceIdentity: string): DetailsState {
  const db = useDb()
  const [amending, setAmending] = useState(false)
  const tryPublicId = looksLikePublicId(serviceIdentity)

  const jobByIdResult = useLiveQuery(
    (q) =>
      q
        .from({ jobs: db.jobsCollection })
        .where(({ jobs }) => eq(jobs.id, serviceIdentity))
        .findOne(),
    [serviceIdentity],
  )

  const jobByPublicIdResult = useLiveQuery(
    (q) =>
      tryPublicId
        ? q
            .from({ jobs: db.jobsCollection })
            .where(({ jobs }) => eq(jobs.publicId, serviceIdentity))
            .findOne()
        : null,
    [serviceIdentity, tryPublicId],
  )

  const job =
    (jobByIdResult.data as Job | undefined) ??
    (jobByPublicIdResult.data as Job | undefined)
  const serviceId = job?.id ?? serviceIdentity

  if (!tryPublicId && job && (jobByIdResult.data as Job | undefined)) {
    captureLegacyIdRoute('job', serviceIdentity)
  }

  const statusChangesResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ statusChanges: db.jobStatusChangesCollection })
            .where(({ statusChanges }) => eq(statusChanges.serviceId, serviceId))
            .orderBy(({ statusChanges }) => statusChanges.createdAt)
        : null,
    [serviceId],
  )

  const addendumsResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ addendums: db.jobAddendumsCollection })
            .where(({ addendums }) => eq(addendums.serviceId, serviceId))
            .orderBy(({ addendums }) => addendums.createdAt)
        : null,
    [serviceId],
  )

  const revisionsResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ revisions: db.jobRevisionsCollection })
            .where(({ revisions }) => eq(revisions.serviceId, serviceId))
            .orderBy(({ revisions }) => revisions.createdAt)
        : null,
    [serviceId],
  )

  const assignmentsResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ assignments: db.jobAssignmentsCollection })
            .where(({ assignments }) => eq(assignments.jobId, serviceId))
        : null,
    [serviceId],
  )

  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))

  const customerResult = useLiveQuery(
    (q) => {
      const customerId = job?.customerId
      if (!customerId) return null
      return q
        .from({ customers: db.customersCollection })
        .where(({ customers }) => eq(customers.id, customerId))
        .findOne()
    },
    [job?.customerId],
  )

  // Archived jobs aren't in the live sync; pull one in on demand.
  const recordQueriesSettled =
    !jobByIdResult.isLoading &&
    !jobByPublicIdResult.isLoading &&
    !jobByIdResult.isError &&
    !jobByPublicIdResult.isError
  const { hydrating } = useEnsureServiceLoaded(
    'job',
    serviceIdentity,
    recordQueriesSettled && !job,
  )

  if (
    jobByIdResult.isLoading ||
    jobByPublicIdResult.isLoading ||
    statusChangesResult.isLoading ||
    customerResult.isLoading ||
    assignmentsResult.isLoading ||
    usersResult.isLoading ||
    hydrating
  ) {
    return { kind: 'loading' }
  }

  if (
    jobByIdResult.isError ||
    jobByPublicIdResult.isError ||
    statusChangesResult.isError ||
    customerResult.isError ||
    assignmentsResult.isError ||
    usersResult.isError
  ) {
    return { kind: 'error' }
  }

  if (!job) {
    return { kind: 'not_found' }
  }

  const customer = customerResult.data as Customer | undefined
  const assignments = (assignmentsResult.data ?? []) as JobAssignment[]
  const usersById = new Map(
    ((usersResult.data ?? []) as { id: string; name: string; email: string }[]).map((user) => [user.id, user]),
  )
  const currentAssignment = assignments
    .filter((assignment) => !assignment.unassignedAt)
    .sort((a, b) => (b.assignedAt ?? b.createdAt).localeCompare(a.assignedAt ?? a.createdAt))[0]
  const assignedUser = currentAssignment ? usersById.get(currentAssignment.userId) : undefined
  const assignmentHistory: JobAssignmentHistoryItem[] = assignments
    .slice()
    .sort((a, b) => (b.assignedAt ?? b.createdAt).localeCompare(a.assignedAt ?? a.createdAt))
    .map((assignment) => ({
      id: assignment.id,
      assignedAt: assignment.assignedAt ?? assignment.createdAt,
      unassignedAt: assignment.unassignedAt,
      assignedUserName: usersById.get(assignment.userId)?.name ?? 'Unknown user',
      authorName: db.getAuthorDisplayName(assignment.authorId),
    }))

  const handleSaveDetails = (payload: JobDetailsSavePayload) => {
    void db.updateJobAndWait?.(job.id, payload)
  }

  const handleChangeCustomer = (customerId: string) =>
    db.updateJobAndWait?.(job.id, { customerId })

  const handleChangeStatus = (status: JobStatus, reason?: string) =>
    db.updateJobAndWait?.(job.id, {
      status,
      statusReason: reason?.trim() || undefined,
    })

  const handleAssignJob = (userId: string) => db.assignJobAndWait?.(job.id, userId)

  const addendums = orderAddendums(
    (addendumsResult.data as Addendum[] | undefined) ?? [],
  )

  // The stored job still holds the values as locked; addendums layer on top to
  // give what is in force now.
  const effective = applyAddendums(
    job as unknown as Record<string, unknown>,
    addendums,
  ) as unknown as Job

  const handleSaveAddendum = async (
    reason: string,
    changes: AddendumChange[],
  ) => {
    await db.appendJobAddendumAndWait(job.id, { reason, changes })
    setAmending(false)
  }

  return {
    kind: 'ready',
    db,
    screenProps: {
      serviceId: job.id,
      createdAt: job.createdAt,
      publicId: job.publicId,
      customerId: job.customerId,
      customerPublicId: customer?.publicId,
      customerName: customer?.fullName ?? 'Unknown customer',
      checkedInSignatureUrl: getJobFileUrl(job, job.checkedInSignature),
      checkedOutSignatureUrl: getJobFileUrl(job, job.checkedOutSignature),
      linkedCustomer: customer
        ? {
            fullName: customer.fullName,
            externalId: customer.externalId,
            contactEmail: customer.contactEmail,
            contactPhone: customer.contactPhone,
            contactAddress: customer.contactAddress,
            invoiceName: customer.invoiceName,
            invoiceEmail: customer.invoiceEmail,
            invoicePhone: customer.invoicePhone,
            invoiceAddress: customer.invoiceAddress,
            invoiceVat: customer.invoiceVat,
          }
        : undefined,
      contactAddress: effective.contactAddress,
      contactName: effective.contactName,
      contactPhone: effective.contactPhone,
      contactEmail: effective.contactEmail,
      invoiceName: effective.invoiceName,
      invoiceAddress: effective.invoiceAddress,
      invoiceEmail: effective.invoiceEmail,
      invoicePhone: effective.invoicePhone,
      invoiceVat: effective.invoiceVat,
      deviceType: effective.deviceType,
      deviceDescription: effective.deviceDescription,
      // Not amendable: the tag owns a unique index and the password is stored
      // encrypted, so both need a real record write.
      devicePassword: job.devicePassword,
      numberTag: job.numberTag,
      fault: effective.fault,
      peripherals: effective.peripherals,
      status: job.status,
      statusTransitions: getAllowedJobStatusTransitions(job.status),
      statusChanges: statusChangesResult.data as StatusChangeLog[] | undefined,
      checkedInSignedAt: job.checkedInSignedAt,
      checkedInBy: job.checkedInBy,
      checkedInByName: job.checkedInBy ? db.getAuthorDisplayName(job.checkedInBy) : undefined,
      checkedInNoSignatureReason: job.checkedInNoSignatureReason,
      checkedOutSignedAt: job.checkedOutSignedAt,
      checkedOutBy: job.checkedOutBy,
      checkedOutByName: job.checkedOutBy ? db.getAuthorDisplayName(job.checkedOutBy) : undefined,
      checkedOutName: job.checkedOutName,
      checkedOutPhone: job.checkedOutPhone,
      checkedOutNoSignatureReason: job.checkedOutNoSignatureReason,
      assignedUserId: currentAssignment?.userId,
      assignedUserName: assignedUser?.name,
      assignmentHistory,
      billingOutcome: job.billingOutcome,
      invoiceId: effective.invoiceId,
      getAuthorDisplayName: db.getAuthorDisplayName,
      onAssignJob: handleAssignJob,
      onSaveDetails: handleSaveDetails,
      onChangeStatus: handleChangeStatus,
      onChangeCustomer: handleChangeCustomer,
      addendums,
      revisions: (revisionsResult.data as ServiceRevision[] | undefined) ?? [],
      lockedValues: {
        fault: job.fault,
        deviceType: job.deviceType,
        deviceDescription: job.deviceDescription,
        peripherals: job.peripherals,
        contactAddress: job.contactAddress,
        contactName: job.contactName,
        contactPhone: job.contactPhone,
        contactEmail: job.contactEmail,
        invoiceName: job.invoiceName,
        invoiceEmail: job.invoiceEmail,
        invoicePhone: job.invoicePhone,
        invoiceAddress: job.invoiceAddress,
        invoiceVat: job.invoiceVat,
        invoiceId: job.invoiceId,
      },
      amending,
      onStartAmend: () => setAmending(true),
      onDiscardAmend: () => setAmending(false),
      onSaveAddendum: handleSaveAddendum,
    },
  }
}
